'use client';

import {
  Accordion,
  Box,
  Flex,
  Link,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaPinterest,
  FaYoutube,
} from 'react-icons/fa6';
import { useConsultation } from '@/contexts/ConsultationContext';
import { useQuoteRequest } from '@/contexts/QuoteRequestContext';
import { isPublished, LEGAL_DOCUMENTS } from '@/config/legalDocuments';

const FooterData = [
  {
    Title: 'A Marca',
    links: [
      { name: 'Quem Somos', url: '/quem-somos' },
      { name: 'Contactos', url: '/lojas' },
      { name: 'Lojas', url: '/lojas' },
      { name: 'Blog', url: '/blog' },

    ],
  },
  {
    Title: 'Serviços',
    links: [
      { name: 'Consultas de Contactologia', url: '/consultas-de-contactologia' },
      { name: 'Exames Ortópticos', url: '/exames-ortopticos' },
      { name: 'Medição da Tensão Ocular', url: '/medicao-da-tensao-ocular' },
      { name: 'Reparação de Óculos', url: '/reparacao-de-oculos' },
    ],
  },
  {
    Title: 'Área do Cliente',
    links: [
      { name: 'A minha conta', url: '/AreaCliente/DadosPessoais' },
      {
        name: 'Marcar Consulta',
        url: '',
        action: 'consultation' as const,
      },
      {
        name: 'Pedir Orçamento',
        url: '',
        action: 'quote' as const,
      },
    ],
  },
  {
    Title: 'Informação Legal',
    links: [
      {
        name: 'Politicas de Privacidade',
        url: '/Informacoes/PoliticasPrivacidade',
      },
      {
        name: 'Politicas de Cookies',
        url: '/Informacoes/PoliticasCookies',
      },
      {
        name: 'Avisos Legais',
        url: '/Informacoes/AvisosLegais',
      },
      {
        name: 'Intermediário de Crédito',
        url: '/Informacoes/IntermediarioCredito',
      },
      {
        name: LEGAL_DOCUMENTS.general.title,
        url: LEGAL_DOCUMENTS.general.path,
      },
      // Só aparece quando o texto das condições Meta estiver publicado.
      ...(isPublished(LEGAL_DOCUMENTS.meta)
        ? [{ name: 'Condições de Venda Ray-Ban Meta', url: LEGAL_DOCUMENTS.meta.path }]
        : []),
      {
        name: 'Livre resolução e devoluções',
        url: '/livre-resolucao',
      },
    ],
  },
];

const SocialButton = ({
  href,
  icon,
  label,
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
}) => (
  <Link
    href={href}
    target="_blank"
    aria-label={label}
    display="flex"
    alignItems="center"
    justifyContent="center"
    w="38px"
    h="38px"
    borderRadius="full"
    bg="rgba(0,176,202,0.12)"
    border="1px solid rgba(0,176,202,0.2)"
    color="#00B0CA"
    transition="all 0.22s"
    _hover={{
      bg: '#00B0CA',
      color: '#0A1B2B',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0,176,202,0.35)',
    }}
  >
    {icon}
  </Link>
);

const Footer = () => {
  const breakpointValue = useBreakpointValue({ base: 'mobile', md: 'desktop' });

  // No servidor (e no primeiro render no cliente, antes de montar) o
  // useBreakpointValue não sabe a largura real do ecrã e assume 'desktop'
  // como omissão — sem isto, um telemóvel via sempre um instante os
  // acordeões todos abertos (versão desktop) antes de fecharem sozinhos.
  // Ao só confiar no breakpoint depois de montado, a primeira renderização
  // é sempre a versão fechada/mobile, em qualquer ecrã.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);
  const value = hasMounted ? breakpointValue : 'mobile';

  const { openModal } = useConsultation();
  const { openModal: openQuoteModal } = useQuoteRequest();

  return (
    <Flex width="full" flexDir="column" align="center">
      <Flex
        width="100%"
        maxW="breakpoint-xl"
        bg={{ base: '#0A1B2B', _dark: '#060F18' }}
        mt="32px"
        borderRadius={{ base: '0', md: '24px' }}
        mb="20px"
        flexDir="column"
        overflow="hidden"
        border="1px solid rgba(0,176,202,0.1)"
        boxShadow="0 8px 48px rgba(0,0,0,0.22)"
      >
        {/* Top accent bar */}
        <Box
          h="3px"
          style={{ background: 'linear-gradient(to right, transparent, #00B0CA, transparent)' }}
          w="full"
        />

        {/* Main footer content */}
        <Flex
          flexDir={{ base: 'column', md: 'row' }}
          gap={{ base: '36px', md: '0' }}
          px={{ base: '24px', md: '48px' }}
          pt={{ base: '36px', md: '48px' }}
          pb={{ base: '32px', md: '40px' }}
          align={{ base: 'center', md: 'flex-start' }}
        >
          {/* Brand column */}
          <Flex
            flexDir="column"
            align={{ base: 'center', md: 'flex-start' }}
            flexShrink={0}
            w={{ base: '100%', md: '220px' }}
            mr={{ base: '0', md: '48px' }}
          >
            <Image
              src='/ido-logo.png'
              alt="Logo idO"
              width={90}
              height={50}
              loading="lazy"
              quality={100}
            />
            <Text
              mt="16px"
              fontSize="13px"
              color="rgba(225,239,245,0.6)"
              lineHeight="1.7"
              textAlign={{ base: 'center', md: 'left' }}
              maxW="200px"
            >
              Ver melhor, muda tudo.
            </Text>

            {/* Social icons */}
            <Flex gap="10px" mt="20px">
              <SocialButton
                href="https://www.facebook.com/Ido.ptOficial/"
                label="Facebook"
                icon={<FaFacebook size={16} />}
              />
              <SocialButton
                href="https://www.instagram.com/ido_pt/"
                label="Instagram"
                icon={<FaInstagram size={16} />}
              />
              <SocialButton
                href="https://www.youtube.com/@ido-pt"
                label="YouTube"
                icon={<FaYoutube size={16} />}
              />
              <SocialButton
                href="https://www.linkedin.com/company/ido-pt"
                label="LinkedIn"
                icon={<FaLinkedin size={16} />}
              />
              <SocialButton
                href="https://pt.pinterest.com/ido_pt/"
                label="Pinterest"
                icon={<FaPinterest size={16} />}
              />
            </Flex>

            {/* Badges */}
            <Flex gap="5px" mt="20px" flexWrap="wrap" justify={{ base: 'center', md: 'flex-start' }}>
              <Image src="/footer/pmelider.png" alt="PME Líder" width={36} height={52} loading="lazy" quality={100} />
              <Image src="/footer/pmeexcelencia.png" alt="PME Excelência" width={36} height={52} loading="lazy" quality={100} />
              <Image src="/footer/quality-award.png" alt="Quality Award" width={22} height={52} loading="lazy" quality={100} style={{ objectFit: 'contain' }} />
              <Image src="/footer/P5ER2025.png" alt="5 Estrelas" width={36} height={46} loading="lazy" quality={100} style={{ objectFit: 'contain' }} />
              <Image src="/footer/cite.png" alt="CITE" width={64} height={46} loading="lazy" quality={100} style={{ objectFit: 'contain' }} />
            </Flex>
          </Flex>

          {/* Vertical divider (desktop) */}
          <Box
            display={{ base: 'none', md: 'block' }}
            w="1px"
            alignSelf="stretch"
            bg="rgba(0,176,202,0.12)"
            mr="48px"
            flexShrink={0}
          />

          {/* Nav links */}
          <Accordion.Root
            flex="1"
            display="flex"
            flexWrap="wrap"
            gap={{ base: '24px', md: '0' }}
            justifyContent={{ base: 'center', md: 'space-between' }}
            multiple
            variant="plain"
            value={value === 'desktop' ? FooterData.map((s) => s.Title) : undefined}
            defaultValue={value === 'desktop' ? FooterData.map((s) => s.Title) : undefined}
            disabled={value === 'desktop'}
            w={{ base: '100%', md: 'auto' }}
          >
            {FooterData.map((section) => (
              <Accordion.Item
                value={section.Title}
                key={section.Title}
                display="flex"
                flexDir="column"
                alignItems={{ base: 'center', md: 'flex-start' }}
                w={{ base: '100%', md: 'auto' }}
                minW={{ md: '140px' }}
                disabled={value === 'desktop'}
              >
                <Accordion.ItemTrigger
                  alignItems={{ base: 'center', md: 'flex-start' }}
                  w={{ base: '100%', md: 'auto' }}
                  justifyContent={{ base: 'center', md: 'flex-start' }}
                  pos="relative"
                  pb="10px"
                  _disabled={{ opacity: 1, cursor: 'default', pointerEvents: 'none' }}
                >
                  <Text
                    fontSize="13px"
                    fontWeight="700"
                    letterSpacing="0.08em"
                    textTransform="uppercase"
                    color="#00B0CA"
                    textAlign={{ base: 'center', md: 'left' }}
                  >
                    {section.Title}
                  </Text>
                  {value !== 'desktop' && <Accordion.ItemIndicator pos="absolute" right="10px" color="#00B0CA" />}
                </Accordion.ItemTrigger>

                {section.links.map((link) => (
                  <Accordion.ItemContent key={link.name}>
                    <Accordion.ItemBody pb="6px">
                      {'action' in link && link.action === 'consultation' ? (
                        <Link
                          as="button"
                          type="button"
                          onClick={() => openModal()}
                          fontSize="13px"
                          color="rgba(225,239,245,0.65)"
                          cursor="pointer"
                          display="block"
                          textAlign={{ base: 'center', md: 'left' }}
                          transition="all 0.18s"
                          pr="3px"
                          _hover={{ color: '#00B0CA', transform: 'translateX(3px)', }}
                        >
                          {link.name}
                        </Link>
                      ) : 'action' in link && link.action === 'quote' ? (
                        <Link
                          as="button"
                          type="button"
                          onClick={openQuoteModal}
                          fontSize="13px"
                          color="rgba(225,239,245,0.65)"
                          cursor="pointer"
                          display="block"
                          textAlign={{ base: 'center', md: 'left' }}
                          transition="all 0.18s"
                          pr="3px"
                          _hover={{ color: '#00B0CA', transform: 'translateX(3px)', }}
                        >
                          {link.name}
                        </Link>
                      ) : (
                        <Link
                          href={link.url}
                          fontSize="13px"
                          color="rgba(225,239,245,0.65)"
                          cursor="pointer"
                          display="block"
                          textAlign={{ base: 'center', md: 'left' }}
                          transition="all 0.18s"
                          pr="3px"
                          _hover={{ color: '#00B0CA', transform: 'translateX(3px)', }}
                        >
                          {link.name}
                        </Link>
                      )}
                    </Accordion.ItemBody>
                  </Accordion.ItemContent>
                ))}
              </Accordion.Item>
            ))}
          </Accordion.Root>
        </Flex>

        {/* Bottom section divider */}
        <Box mx="24px" h="1px" bg="rgba(0,176,202,0.1)" />

        {/* Payment, Shipping, Security, Complaints */}
        <Flex
          px={{ base: '24px', md: '48px' }}
          py={{ base: '24px', md: '28px' }}
          gap={{ base: '24px', md: '40px' }}
          flexDir={{ base: 'column', md: 'row' }}
          align={{ base: 'center', md: 'flex-start' }}
          justify={{ base: 'center', md: 'space-between' }}
          flexWrap="wrap"
        >
          {/* Payment methods */}
          <Flex flexDir="column" align={{ base: 'center', md: 'flex-start' }} gap="10px">
            <Text fontSize="11px" fontWeight="700" letterSpacing="0.08em" textTransform="uppercase" color="rgba(225,239,245,0.5)">
              Formas de Pagamento
            </Text>
            <Flex gap="8px" flexWrap="wrap" justify={{ base: 'center', md: 'flex-start' }}>
              {['visa-svg.svg', 'mastercard-svg.svg', 'maestro-svg.svg', 'mb-multibanco-svg.svg',].map((file) => (
                <Box key={file} bg="rgba(255,255,255,0.06)" borderRadius="10px" p="5px 10px" border="1px solid rgba(255,255,255,0.08)">
                  <Image src={`/footer/${file}`} alt={file.replace('-svg.svg', '')} width={44} height={28} loading="lazy" quality={85} style={{ objectFit: 'contain', height: '28px', width: 'auto' }} />
                </Box>
              ))}
            </Flex>
            <Flex gap="8px" flexWrap="wrap" justify={{ base: 'center', md: 'flex-start' }}>
              {[ 'mb-may-svg.svg', 'paypal-svg.svg', 'apple-pay.svg', 'google-pay.svg'].map((file) => (
                <Box key={file} bg="rgba(255,255,255,0.06)" borderRadius="10px" p="5px 10px" border="1px solid rgba(255,255,255,0.08)">
                  <Image src={`/footer/${file}`} alt={file.replace('-svg.svg', '')} width={44} height={28} loading="lazy" quality={85} style={{ objectFit: 'contain', height: '28px', width: 'auto' }} />
                </Box>
              ))}
            </Flex>
          </Flex>

          {/* Shipping */}
          <Flex flexDir="column" align={{ base: 'center', md: 'flex-start' }} gap="10px">
            <Text fontSize="11px" fontWeight="700" letterSpacing="0.08em" textTransform="uppercase" color="rgba(225,239,245,0.5)">
              Meios de Envio
            </Text>
            <Box bg="rgba(255,255,255,0.06)" borderRadius="10px" p="6px 12px" border="1px solid rgba(255,255,255,0.08)">
              <Image src="/footer/ctt.webp" alt="CTT" width={90} height={36} loading="lazy" quality={85} style={{ objectFit: 'contain', height: '36px', width: 'auto' }} />
            </Box>
          </Flex>

          {/* Loja certificada Ray-Ban */}
          <Flex flexDir="column" align={{ base: 'center', md: 'flex-start' }} gap="10px">
            <Text fontSize="11px" fontWeight="700" letterSpacing="0.08em" textTransform="uppercase" color="rgba(225,239,245,0.5)">
              Loja Certificada
            </Text>
            <Flex align="center" gap="10px" bg="rgba(255,255,255,0.06)" borderRadius="10px" p="6px 12px" border="1px solid rgba(255,255,255,0.08)">
              <Image src="/badges/rayban-certified-seal.png" alt="Loja certificada Ray-Ban 2026" width={36} height={36} loading="lazy" style={{ height: '36px', width: '36px' }} />
              <Flex flexDir="column" lineHeight="1.2">
                <Text fontSize="12px" fontWeight="700" color="rgba(225,239,245,0.9)">Ray-Ban</Text>
                <Text fontSize="11px" color="rgba(225,239,245,0.55)">2026</Text>
              </Flex>
            </Flex>
          </Flex>

          {/* Security */}
          <Flex flexDir="column" align={{ base: 'center', md: 'flex-start' }} gap="10px">
            <Text fontSize="11px" fontWeight="700" letterSpacing="0.08em" textTransform="uppercase" color="rgba(225,239,245,0.5)">
              Segurança
            </Text>
            <Box bg="rgba(255,255,255,0.06)" borderRadius="10px" p="6px 12px" border="1px solid rgba(255,255,255,0.08)">
              <Image src="/footer/ssl.webp" alt="SSL Certificado" width={200} height={44} loading="lazy" quality={85} style={{ objectFit: 'contain', height: '44px', width: 'auto' }} />
            </Box>
          </Flex>

          {/* Livro de Reclamações + entidade de resolução alternativa de
              litígios (Lei 144/2015 — substitui a plataforma RLL da UE,
              suprimida a 20/07/2025) */}
          <Flex flexDir="column" align={{ base: 'center', md: 'flex-start' }} gap="10px">
            <Text fontSize="11px" fontWeight="700" letterSpacing="0.08em" textTransform="uppercase" color="rgba(225,239,245,0.5)">
              Livro de Reclamações
            </Text>
            <Link href="https://www.livroreclamacoes.pt/inicio/" target="_blank">
              <Box bg="white" borderRadius="10px" p="4px 8px" border="1px solid rgba(255,255,255,0.12)" transition="all 0.2s" _hover={{ transform: 'scale(1.03)' }}>
                <Image src="/footer/livro_reclamacoes-500x-207.webp" alt="Livro de Reclamações" width={90} height={46} loading="lazy" quality={85} style={{ objectFit: 'contain', height: '46px', width: 'auto' }} />
              </Box>
            </Link>
            <Text fontSize="11px" color="rgba(225,239,245,0.5)" maxW="220px" textAlign={{ base: 'center', md: 'left' }} lineHeight="1.5">
              Resolução alternativa de litígios:{' '}
              <Link href="https://www.cniacc.pt" target="_blank" rel="noopener noreferrer" color="#00B0CA" _hover={{ color: '#00D4F5' }}>
                CNIACC
              </Link>
            </Text>
          </Flex>
        </Flex>

        {/* Copyright bar */}
        <Box mx="24px" h="1px" bg="rgba(0,176,202,0.08)" />
        <Flex
          px={{ base: '24px', md: '48px' }}
          py="18px"
          flexDir={{ base: 'column', md: 'row' }}
          align="center"
          justify={{ base: 'center', md: 'space-between' }}
          gap="8px"
        >
          <Text fontSize="11px" color="rgba(225,239,245,0.35)" textAlign={{ base: 'center', md: 'left' }}>
            &copy; {new Date().getFullYear()} idO. Todos os direitos reservados.
          </Text>
          <Flex gap="12px" flexWrap="wrap" justify="center">
            <Text fontSize="11px" color="rgba(225,239,245,0.3)" textAlign="center">
              Site protegido por reCAPTCHA —{' '}
              <Link href="https://policies.google.com/privacy" target="_blank" color="#00B0CA" _active={{ color: "#00B0CA" }} _focus={{ color: "#00B0CA" }} _hover={{ color: '#00B0CA' }} textDecoration="underline">
                Privacidade
              </Link>
              {' & '}
              <Link href="https://policies.google.com/terms" target="_blank" color="#00B0CA" _active={{ color: "#00B0CA" }} _focus={{ color: "#00B0CA" }} _hover={{ color: '#00B0CA' }} textDecoration="underline">
                Termos
              </Link>
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default Footer;
